import React, { useMemo } from 'react'
import {
  Box,
  useTokens,
  Link,
  Typography,
  Alert,
  Button,
  ListItem,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@barracuda-internal/bds-core-v2'
import { useFormatMessage } from 'lib/localization'
import SetupFlowStepsContentContainer from '../../../layout/SetupFlowStepsContentContainer'
import ContentTitleComponent from '../../../components/ContentTitleComponent'
import ErrorComponent from '../../../components/ErrorComponent'
import { useVerifyMsDomainLogic } from './useVerifyMsDomainLogic'
import ConfirmInlineDeploymentDialog from './ConfirmInlineDeploymentDialog'

const BASE_I18N_KEY = 'ess_wizard.verify_domain'

const VerifyMsDomain = () => {
  const tokens = useTokens()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  const [state, eventHandlers] = useVerifyMsDomainLogic()

  return useMemo(
    () => (
      <Box sx={{ height: '100%' }}>
        {state.isMultiDomainEnabled && state.showConfirmDialog && (
          <ConfirmInlineDeploymentDialog
            domains={state.msDomainNames}
            onConfirm={eventHandlers.onConfirmDeployment}
            onCancel={eventHandlers.onCancelDeployment}
          />
        )}
        <SetupFlowStepsContentContainer>
          <ContentTitleComponent
            title={formatMessage('title')}
            subtitle={formatMessage(`${state.availableDomains.length === 0 ? 'support_subtitle' : 'subtitle'}`, {
              a: (txt: any): any => (
                <Link target="_blank" href="https://www.barracuda.com/support/contact">
                  {txt}
                </Link>
              )
            })}
          />
          <ErrorComponent />
          {state.availableDomains.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                padding: tokens.aliasesSpaceContainerDefault,
                gap: tokens.aliasesSpaceContainerDefault,
                flexDirection: 'column'
              }}
            >
              {state.unavailableDomains.length > 0 && (
                <Typography
                  sx={{
                    fontFamily: tokens.aliasesFontFamilyDefault,
                    fontSize: tokens.aliasesFontSizeH4,
                    fontWeight: tokens.aliasesFontWeightNeutral,
                    color: tokens.aliasesColorTextStaticEmphasis,
                    lineHeight: tokens.aliasesLineHeightH4
                  }}
                >
                  {formatMessage('available_domains')}
                </Typography>
              )}

              {state.isMultiDomainEnabled ? (
                <>
                  {/* Multi-select mode: Info Banner */}
                  <Alert bdsType="neutralInfo" bdsBoxShadowType="inline">
                    <Box component="ul" sx={{ margin: 0, paddingLeft: tokens.aliasesSpaceContainerDefault }}>
                      <li>{formatMessage('info_bullet_single')}</li>
                      <li>{formatMessage('info_bullet_multiple')}</li>
                    </Box>
                  </Alert>

                  {/* Multi-select mode: Select All row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.aliasesSpaceContainerDense,
                      paddingBottom: tokens.aliasesSpaceContainerDense,
                      paddingLeft: tokens.aliasesSpaceContainerDense
                    }}
                  >
                    <Checkbox
                      checked={state.msDomainNames.length === state.availableDomains.length}
                      indeterminate={
                        state.msDomainNames.length > 0 && state.msDomainNames.length < state.availableDomains.length
                      }
                      onChange={() => {
                        if (state.msDomainNames.length === state.availableDomains.length) {
                          eventHandlers.onDeselectAll()
                        } else {
                          eventHandlers.onSelectAll()
                        }
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: tokens.aliasesFontFamilyDefault,
                        fontSize: tokens.aliasesFontSizeDefault,
                        fontWeight: tokens.aliasesFontWeightNeutral,
                        color: tokens.aliasesColorTextStaticEmphasis,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (state.msDomainNames.length === state.availableDomains.length) {
                          eventHandlers.onDeselectAll()
                        } else {
                          eventHandlers.onSelectAll()
                        }
                      }}
                    >
                      {formatMessage('select_all')}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: tokens.aliasesFontFamilyDefault,
                        fontSize: tokens.aliasesFontSizeCaption,
                        fontWeight: tokens.aliasesFontWeightNeutral,
                        color: tokens.aliasesColorTextStaticSubtle
                      }}
                    >
                      {formatMessage('selected_count', {
                        count: state.msDomainNames.length,
                        total: state.availableDomains.length
                      })}
                    </Typography>
                  </Box>

                  {/* Multi-select mode: Checkbox list */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexWrap: 'nowrap',
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}
                  >
                    {state.availableDomains.map((name, index) => (
                      <ListItem
                        key={name}
                        bdsType="staticSubtle"
                        bottomBorder={index !== state.availableDomains.length - 1}
                        bdsPadding="dense"
                        onClick={() => eventHandlers.onToggleDomain(name)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: tokens.aliasesSpaceContainerDense }}>
                          <Checkbox
                            checked={state.msDomainNames.includes(name)}
                            onClick={e => e.stopPropagation()}
                            onChange={() => eventHandlers.onToggleDomain(name)}
                          />
                          <Typography>{name}</Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </Box>
                </>
              ) : (
                /* Single-select mode: RadioGroup */
                <RadioGroup
                  value={state.msDomainName ?? state.msDomainNames[0] ?? ''}
                  onChange={eventHandlers.onSelectChange}
                  sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'nowrap' }}
                >
                  {state.availableDomains.map((name, index) => (
                    <ListItem
                      key={name}
                      bdsType="staticSubtle"
                      bottomBorder={index !== state.availableDomains.length - 1}
                      bdsPadding="dense"
                      bdsSize="small"
                    >
                      <FormControlLabel
                        control={<Radio bdsSize="small" />}
                        variant="toggle"
                        label={name}
                        value={name}
                      />
                    </ListItem>
                  ))}
                </RadioGroup>
              )}
              {/* Item #6: Typography hierarchy - title more prominent, body less emphasized */}
              <Box
                sx={{
                  paddingTop: tokens.aliasesSpaceContainerDefault
                }}
              >
                <Typography
                  sx={{
                    fontFamily: tokens.aliasesFontFamilyDefault,
                    fontSize: tokens.aliasesFontSizeDefault,
                    fontWeight: tokens.aliasesFontWeightEmphasis,
                    color: tokens.aliasesColorTextStaticEmphasis,
                    lineHeight: tokens.aliasesLineHeightDefault
                  }}
                >
                  {formatMessage('why_not_seeing_title')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: tokens.aliasesFontFamilyDefault,
                    fontSize: tokens.aliasesFontSizeCaption,
                    fontWeight: tokens.aliasesFontWeightNeutral,
                    color: tokens.aliasesColorTextStaticSubtle,
                    lineHeight: tokens.aliasesLineHeightCaption,
                    paddingTop: tokens.aliasesSpaceContainerDense
                  }}
                  component="div"
                >
                  {formatMessage('why_not_seeing_content', { br: () => <br /> })}
                </Typography>
              </Box>
            </Box>
          )}

          {state.unavailableDomains.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                paddingLeft: tokens.aliasesSpaceContainerDefault,
                paddingRight: tokens.aliasesSpaceContainerDefault,
                paddingBottom: tokens.aliasesSpaceContainerDefault,
                gap: tokens.aliasesSpaceContainerDefault,
                flexDirection: 'column'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: tokens.aliasesFontFamilyDefault,
                    fontSize: tokens.aliasesFontSizeH4,
                    fontWeight: tokens.aliasesFontWeightNeutral,
                    color: tokens.aliasesColorTextStaticEmphasis,
                    lineHeight: tokens.aliasesLineHeightH4
                  }}
                >
                  {formatMessage('unavailable_domains')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: tokens.aliasesFontFamilyDefault,
                    fontSize: tokens.aliasesFontSizeCaption,
                    fontWeight: tokens.aliasesFontWeightNeutral,
                    color: tokens.aliasesColorTextStaticSubtle,
                    lineHeight: tokens.aliasesLineHeightCaption
                  }}
                >
                  {formatMessage('unavailable_domains_subtitle')}
                </Typography>
              </Box>
              <Box>
                {state.unavailableDomains.map((domain, index) => (
                  <ListItem
                    key={domain.domain}
                    bdsType="staticSubtle"
                    bottomBorder={index !== state.unavailableDomains.length - 1}
                    bdsPadding="dense"
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography>{domain.domain}</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography>{domain.account_name}</Typography>
                      <Typography
                        sx={{
                          fontFamily: tokens.aliasesFontFamilyDefault,
                          fontSize: tokens.aliasesFontSizeCaption,
                          fontWeight: tokens.aliasesFontWeightNeutral,
                          color: tokens.aliasesColorTextStaticSubtle,
                          lineHeight: tokens.aliasesLineHeightCaption
                        }}
                      >
                        {domain.portal_id}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </Box>
              <Box
                sx={{
                  paddingTop: tokens.aliasesSpaceContainerDefault
                }}
              >
                <Alert
                  bdsType="neutralInfo"
                  bdsBoxShadowType="inline"
                  action={
                    <Button
                      bdsType="interactiveSubtle"
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() =>
                        window.open('https://www.barracuda.com/support/contact', '_blank', 'noopener,noreferrer')
                      }
                    >
                      {formatMessage('contact_support')}
                    </Button>
                  }
                >
                  {formatMessage('contact_info')}
                </Alert>
              </Box>
            </Box>
          )}
        </SetupFlowStepsContentContainer>
      </Box>
    ),
    [tokens, formatMessage, state, eventHandlers]
  )
}

export default VerifyMsDomain
